import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '首页', to: '/' },
  { label: '资讯', to: '/news' },
  { label: '娱乐', to: '/entertainment' },
  { label: '生活', to: '/lifestyle' },
  { label: '关于', to: '/about' },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    if (!kw) return;
    navigate(`/search?q=${encodeURIComponent(kw)}`);
    setKeyword('');
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b bg-background-50/95 backdrop-blur transition-shadow ${
        scrolled ? 'border-background-200' : 'border-transparent'
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-[1280px] items-center justify-between gap-4 px-4 md:px-6">
        <Link to="/" className="flex flex-shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 font-heading text-lg font-bold text-background-50 dark:text-foreground-950">
            小
          </span>
          <span className="hidden flex-col leading-none sm:flex">
            <span className="font-heading text-base font-bold text-foreground-950">小</span>
            <span className="text-[11px] text-foreground-500">多元内容聚合</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive(item.to)
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100 hover:text-primary-600'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={handleSearch} className="hidden lg:flex">
          <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2 focus-within:border-primary-400">
            <i className="ri-search-line text-base text-foreground-400"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="search"
              placeholder="搜索全站内容"
              className="w-40 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
          </div>
        </form>

        <div className="flex items-center gap-2 md:hidden">
          <Link
            to="/search"
            aria-label="搜索"
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100"
          >
            <i className="ri-search-line text-xl"></i>
          </Link>
          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100"
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        <Link
          to="/search"
          aria-label="搜索"
          className="hidden h-10 w-10 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100 md:flex lg:hidden"
        >
          <i className="ri-search-line text-xl"></i>
        </Link>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 md:hidden">
          <form onSubmit={handleSearch} className="mb-3">
            <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2.5">
              <i className="ri-search-line text-base text-foreground-400"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="search"
                placeholder="搜索全站内容"
                className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>
          </form>
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors ${
                  isActive(item.to)
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}